import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { assetsApi } from "../api/assets";
import type { ApiError } from "../api/client";
import { scannedAssetCode } from "../lib/assetCode";

/**
 * `/a/:tag` — the QR landing route: the label URL carries the system id (A-00000053),
 * older labels the tag; the server resolves either. Replaces itself with the karton;
 * a 404 offers a manual input (code, tag or a pasted label URL).
 */
export default function AssetByTagPage() {
  const { tag = "" } = useParams<{ tag: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [manual, setManual] = useState("");

  useEffect(() => {
    if (!tag) return;
    let cancelled = false;
    setError(null);
    setNotFound(false);
    // A camera app may open the label URL itself; a scanner may pass it through — both reduce to the code.
    assetsApi.byTag(scannedAssetCode(tag))
      .then((d) => { if (!cancelled) navigate(`/asseti/${d.id}`, { replace: true }); })
      .catch((err: ApiError) => {
        if (cancelled) return;
        if (err.status === 404) setNotFound(true);
        else setError(err.message || t("assets.byTag.failed", "Traženje nije uspelo — pokušajte ponovo ili unesite kod ručno."));
      });
    return () => { cancelled = true; };
  }, [tag, navigate]);

  function submit(e: FormEvent) {
    e.preventDefault();
    const next = scannedAssetCode(manual);
    if (next) navigate(`/a/${encodeURIComponent(scannedAssetCode(next))}`);
  }

  return (
    <div className="by-tag-page">
      <div className="card by-tag-card">
        {!notFound && !error ? (
          <div className="empty">{t("assets.byTag.resolving", "Tražim asset {{tag}}…", { tag })}</div>
        ) : (
          <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <h1 style={{ margin: 0, fontSize: 20 }}>
              {notFound
                ? t("assets.byTag.notFoundCode", "Asset sa kodom {{tag}} ne postoji.", { tag })
                : error}
            </h1>
            <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <label className="field">
                <span>{t("assets.byTag.manualCode", "Unesite sistemski ID ili oznaku ručno")}</span>
                <input
                  className="input"
                  value={manual}
                  onChange={(e) => setManual(e.target.value)}
                  placeholder="A-00000053"
                  autoCapitalize="characters"
                  spellCheck={false}
                  style={{ fontFamily: "var(--mono)" }}
                />
              </label>
              <div className="form-actions">
                <Link to="/asseti" className="btn lg" style={{ textDecoration: "none" }}>{t("assets.byTag.toList", "Svi asseti")}</Link>
                <button type="submit" className="btn primary lg" disabled={!manual.trim()}>{t("qr.open", "Otvori")}</button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
